<template>
  <div>
    <!-- Sayfa yüklenirken Preloader göster -->
    <PreLoader v-if="isLoading" />

    <!-- Sayfa tamamen yüklendiğinde içeriği göster -->
    <div v-show="!isLoading">
      <NavbarTop v-if="showNavbar" />
      <router-view />
      <FooterPage v-if="showFooter" />
      <CookieConsent />
      
      <!-- Global ChatBot Gemini (Her sayfada görünür, bağımsızdır) -->
      <ChatBotGemini v-if="!route.path.startsWith('/chat')" />
    </div>
  </div>
</template>

<script setup>
  import { computed, provide, ref, watch, onMounted, onUnmounted } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { getAuth, onAuthStateChanged, signInWithCredential, GoogleAuthProvider } from 'firebase/auth';
  import { doc, getDoc, collection, writeBatch } from 'firebase/firestore';
  import { db, logAnalyticsEvent } from '@/firebase';
  import { useUnreadMessages } from '@/composables/useUnreadMessages';
  import { useUnreadMessagesStore } from '@/stores/unreadMessagesStore';
  import { useUserStore } from '@/stores/userStore';
  import NavbarTop from './components/NavbarTop.vue';
  import FooterPage from './components/FooterPage.vue';
  import CookieConsent from './components/CookieConsent.vue';
  import PreLoader from './components/PreLoader.vue';
  import ChatBotGemini from './components/ChatBotGemini.vue';
  import '@/assets/css/import-icons.css'; // Global CSS dosyanın yolu

  // Vue Store
  const unreadStore = useUnreadMessagesStore();
  const userStore = useUserStore();

  // Loading Durumu
  const isLoading = ref(true);
  const startTime = ref(performance.now());
  const auth = getAuth();
  const route = useRoute();
  const router = useRouter();
  
  // -- Google One Tap Logic --
  const processSocialUser = async (user) => {
    const userRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      const userData = {
        uid: user.uid,
        displayName: user.displayName || null,
        email: user.email,
        photoURL: user.photoURL,
        phoneNumber: user.phoneNumber || null,
        address: null,
        createdAt: new Date(),
        account_number: Date.now(),
      };

      await userStore.setUserData(user.uid, userData);

      const accountCollection = collection(db, 'users', user.uid, 'account');
      const accountDocs = [
        { id: 'properties', type: 'Member', ad_limit: 3 },
        { id: 'pet_account', type: 'Personal' },
        { id: 'service_account', type: 'Personal' },
      ];

      const batch = writeBatch(db);
      accountDocs.forEach((account) => {
        const docRef = doc(accountCollection, account.id);
        batch.set(docRef, account);
      });
      await batch.commit();
    } else {
      await userStore.fetchUserData(user.uid);
    }
  };

  const handleOneTapResponse = async (response) => {
    try {
      const credential = GoogleAuthProvider.credential(response.credential);
      const result = await signInWithCredential(auth, credential);
      await processSocialUser(result.user);
      if (['/login', '/signup'].includes(route.path)) {
        router.push('/');
      }
    } catch (err) {
      console.error("One Tap Error:", err);
    }
  };

  const showGoogleOneTap = () => {
    // Don't show on login/signup pages where we already have a prominent button
    if (['/login', '/signup'].includes(route.path)) return;
    
    const googleClientId = "822930955588-gc28m545nmnbt3p7ofb3fbfutg5t9vqe.apps.googleusercontent.com";
    
    const initAndPrompt = () => {
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: handleOneTapResponse,
        auto_select: false,
        cancel_on_tap_outside: false
      });
      window.google.accounts.id.prompt();
    };

    if (window.google) {
      initAndPrompt();
    } else {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        if (window.google) initAndPrompt();
      };
      document.head.appendChild(script);
    }
  };

  // **Uygulama ilk açıldığında Preloader göster**
  onMounted(() => {
    isLoading.value = true;
    setTimeout(() => (isLoading.value = false), 2000); // İlk açılışta Preloader 2sn görünür
    unreadStore.startListening();
    
    onAuthStateChanged(auth, (user) => {
      if (!user) {
        showGoogleOneTap();
      }
    });
  });

  // **Uygulama kapanınca dinleyicileri temizle**
  onUnmounted(() => unreadStore.stopListening());

  // **Sayfa Değiştiğinde Preloader Göster**
  router.beforeEach((to, from, next) => {
    // Eğer sadece history state değiştiyse (aynı sayfa, aynı filtreler) preloader gösterme
    // Sadece URL parametreleri (query) değiştiyse sayfayı komple yeniden yüklüyormuş gibi preloader gösterme
    if (to.path === from.path) {
      next();
      return;
    }

    isLoading.value = true;
    next();
  });

  // **Sayfa yükleme tamamlandığında Preloader'ı kaldır**
  router.afterEach(() => {
    setTimeout(() => (isLoading.value = false), 500); // 500ms sonra Preloader gizlenir
  });

  // Kullanıcı Okunmamış Mesajları
  const { unreadCount } = useUnreadMessages();
  provide('unreadCount', unreadCount);

  // Navbar ve Footer Kontrolleri
  const showNavbar = computed(() => !['/login', '/signup'].includes(route.path));
  const showFooter = computed(() => {
    const hiddenPaths = [
      '/petmapview',
      '/login',
      '/register',
      '/forgotpassword',
      '/resetpassword',
      '/chat',
      '/signup',
    ];
    if (hiddenPaths.includes(route.path)) return false;
    return !route.matched.some((record) => record.path.includes('/service-details/'));
  });


  // Analytics İzleme
  const getDeviceCategory = () => {
    const width = window.innerWidth;
    return width <= 768 ? 'mobile' : width <= 1024 ? 'tablet' : 'desktop';
  };

  watch(
    () => route.path,
    async (newPath) => {
      try {
        await logAnalyticsEvent('page_view', {
          page_title: route.meta.title || 'Petqueen',
          page_path: newPath,
          page_location: window.location.href,
          load_time: performance.now() - startTime.value,
          timestamp: new Date().toISOString(),
          user_id: auth.currentUser?.uid,
          screen_resolution: `${window.screen.width}x${window.screen.height}`,
          device_category: getDeviceCategory(),
        });
      } catch (error) {
        console.error('Analytics hatası:', error);
      }
    },
    { immediate: true },
  );
</script>
